import React, { useEffect, useState } from "react";
import { RefreshCw, WifiOff, CloudUpload, AlertTriangle } from "lucide-react";
import { sendQueuedWrite } from "@/lib/api";
import { flushQueue, listWrites, retryFailed, subscribeQueue, currentUserId } from "@/lib/offlineQueue";
import { onUpdateReady, applyUpdate, hasWaitingUpdate } from "@/lib/pwa";

// Bottom-of-screen status for three things the family should never miss:
//   1. A new version is waiting. Refresh is offered, and the app never reloads
//      by itself in the middle of a story.
//   2. Progress saved while offline is waiting to sync. A rejected item stays
//      visible with a Retry button.
//   3. The device is offline, so downloaded stories are what works.

export default function PwaNotices() {
  const [updateReady, setUpdateReady] = useState(hasWaitingUpdate());
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState([]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  const refreshQueue = async () => {
    const uid = currentUserId();
    const rows = (await listWrites()).filter((r) => r.uid === uid);
    setPending(rows.filter((r) => r.status === "pending").length);
    setFailed(rows.filter((r) => r.status === "failed"));
  };

  useEffect(() => {
    const offUpdate = onUpdateReady(() => setUpdateReady(true));
    const offQueue = subscribeQueue(refreshQueue);
    const sync = () => { flushQueue(sendQueuedWrite).then(refreshQueue); };
    const goOnline = () => { setOnline(true); sync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    // Not every connection change reaches the "online" event, so also retry
    // every 30 seconds and whenever the app comes back to the foreground.
    // Nothing is replayed while the device is offline, so this is cheap.
    const retry = setInterval(() => { if (navigator.onLine) sync(); }, 30000);
    const onVisible = () => { if (!document.hidden && navigator.onLine) sync(); };
    document.addEventListener("visibilitychange", onVisible);
    refreshQueue();
    if (navigator.onLine) sync();
    return () => {
      offUpdate();
      offQueue();
      clearInterval(retry);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // Sync on mount and on connection changes. refreshQueue is recreated each
    // render, but it only reads storage, so one subscription is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRetry = async () => {
    await retryFailed();
    await flushQueue(sendQueuedWrite);
    refreshQueue();
  };

  return (
    <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 z-40 flex flex-col gap-2 max-w-[calc(100vw-2rem)] sm:max-w-sm pointer-events-none" aria-live="polite">
      {updateReady && !updateDismissed && (
        <div className="pointer-events-auto hv-card p-4 flex items-center gap-3 shadow-lg" data-testid="pwa-update-toast">
          <RefreshCw className="w-5 h-5 text-hv-teal shrink-0" strokeWidth={2.4} />
          <p className="text-sm font-bold text-hv-navy flex-1">New version available</p>
          <button onClick={applyUpdate} className="hv-btn-primary px-3 py-2 text-sm min-h-[40px]" data-testid="pwa-update-refresh">Refresh</button>
          <button onClick={() => setUpdateDismissed(true)} className="text-sm font-bold text-hv-slate px-2 min-h-[40px]" data-testid="pwa-update-later">Later</button>
        </div>
      )}

      {failed.length > 0 && (
        <div className="pointer-events-auto hv-card p-4 flex items-start gap-3 shadow-lg" data-testid="pwa-sync-failed">
          <AlertTriangle className="w-5 h-5 text-hv-coral shrink-0 mt-0.5" strokeWidth={2.4} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-hv-navy">
              {failed.length === 1 ? "1 saved answer couldn't sync" : `${failed.length} saved answers couldn't sync`}
            </p>
            <p className="text-xs text-hv-slate mt-0.5 break-words">{failed[0].error}</p>
          </div>
          <button onClick={onRetry} className="hv-btn-secondary px-3 py-2 text-sm min-h-[40px]" data-testid="pwa-sync-retry">Retry</button>
        </div>
      )}

      {pending > 0 && failed.length === 0 && (
        <div className="pointer-events-auto hv-card px-4 py-3 flex items-center gap-2 shadow-lg" data-testid="pwa-sync-pending">
          <CloudUpload className="w-4 h-4 text-hv-teal shrink-0" strokeWidth={2.4} />
          <p className="text-sm font-bold text-hv-navy">Saved offline — will sync when you're back online</p>
        </div>
      )}

      {!online && (
        <div className="pointer-events-auto hv-card px-4 py-3 flex items-center gap-2 shadow-lg" data-testid="pwa-offline">
          <WifiOff className="w-4 h-4 text-hv-slate shrink-0" strokeWidth={2.4} />
          <p className="text-sm font-bold text-hv-navy">You're offline — downloaded stories still work</p>
        </div>
      )}
    </div>
  );
}
